import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import useServiceEmailNotesService from "../../../services/useServiceEmailNotesService";
import type { ServiceResponse } from "../../../hooks/useFetch";
import showToast from "../../../utils/toast";
import "../styles/ServiceNoteModal.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  emailId: number;
  subject?: string | null;
  onSuccess?: () => void;
};

const ServiceEmailCommentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  emailId,
  subject,
  onSuccess,
}) => {
  const { store, fetching } = useServiceEmailNotesService();
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setText("");
      setError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !fetching.store) onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, fetching.store, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) {
      setError("Escribe el texto del comentario.");
      return;
    }
    setError("");
    store(emailId, { text: trimmed }, {
      onSuccess: (r: ServiceResponse) => {
        if (r?.data) {
          showToast("Comentario agregado", "success");
          onSuccess?.();
          onClose();
        }
      },
      onError: (r: ServiceResponse) => {
        showToast(r?.message || "No se pudo guardar el comentario", "error");
      },
    });
  };

  const content = (
    <div
      className="service-note-overlay"
      onClick={() => {
        if (!fetching.store) onClose();
      }}
    >
      <div className="service-note-content" onClick={(e) => e.stopPropagation()}>
        <div className="service-note-header">
          <h3 className="service-note-title">Nuevo comentario</h3>
          <button
            type="button"
            className="service-note-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={Boolean(fetching.store)}
          >
            <X size={22} />
          </button>
        </div>

        {subject ? (
          <p style={{ margin: "0 0 1rem", color: "#475569", fontSize: "0.95rem", lineHeight: 1.45 }}>
            {subject}
          </p>
        ) : null}

        <form className="service-note-form" onSubmit={handleSubmit}>
          <div className="service-note-form-field">
            <label htmlFor="service-email-note-text">Comentario</label>
            <textarea
              id="service-email-note-text"
              className="service-note-form-textarea"
              rows={4}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError("");
              }}
              placeholder="Escribe un comentario sobre este correo…"
              disabled={Boolean(fetching.store)}
              autoFocus
              maxLength={2000}
            />
            {error ? <span className="service-note-form-error">{error}</span> : null}
          </div>

          <div className="service-note-form-actions">
            <button
              type="button"
              className="service-note-form-btn service-note-form-btn-cancel"
              onClick={onClose}
              disabled={Boolean(fetching.store)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="service-note-form-btn service-note-form-btn-submit"
              disabled={Boolean(fetching.store) || !text.trim()}
            >
              {fetching.store ? "Guardando…" : "Agregar comentario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ServiceEmailCommentModal;
